// SEP-2 / E0 — DestructiveActionGuard: makes destructive owner actions SAFE by default (law §75-80).
// Archive is the primary, reversible action; a hard Delete is guarded behind a focus-trapped
// role="alertdialog" confirmation and is only offered when the caller supplies onDelete. After an
// archive the row exposes an inline Restore ("Undo") affordance announced via an aria-live region, so
// nothing is ever lost by a single click.
//
// Category-agnostic: Shop (archive a product / item / contact / deal) AND Booking (archive a service)
// consume the same guard. Every control names the entity + consequence in its aria-label. All strings
// are i18n KEYS resolved via t() at render.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Archive, ArchiveRestore, Trash2, Loader2, AlertTriangle } from 'lucide-preact';
import { t } from '../lib/i18n';

export interface DestructiveActionGuardProps {
  /** the entity's human name (woven into aria-labels + the confirm dialog copy). */
  entityLabel: string;
  /** whether the row is already archived → show Restore instead of Archive. */
  archived?: boolean;
  /** archive handler (reversible). Return true on success to reveal the inline Undo affordance. */
  onArchive?: () => Promise<boolean> | boolean | void;
  /** restore/undo handler (un-archive). */
  onRestore?: () => Promise<boolean> | boolean | void;
  /** hard-delete handler. When omitted, NO delete control is rendered (archive-only, the safe default). */
  onDelete?: () => Promise<boolean> | boolean | void;
  /** disable all controls while a mutation is in flight elsewhere in the row. */
  busy?: boolean;
  /** confirm dialog copy overrides. */
  confirmTitleKey?: string;
  confirmBodyKey?: string;
}

export function DestructiveActionGuard({
  entityLabel, archived, onArchive, onRestore, onDelete, busy,
  confirmTitleKey = 'own.confirmDeleteT', confirmBodyKey = 'own.confirmDeleteS',
}: DestructiveActionGuardProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  // After a successful archive we show an inline "Archived · Undo" affordance (the undo). It clears
  // once the parent list re-reads (archived flips) or the user restores.
  const [justArchived, setJustArchived] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const disabled = busy || working;

  // Focus-trap + Esc for the confirm alertdialog. Focus moves to the destructive confirm on open and
  // returns to the trigger on close.
  useEffect(() => {
    if (!confirming) return;
    confirmBtnRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'Tab') {
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirming]);

  function close(): void {
    setConfirming(false);
    triggerRef.current?.focus();
  }

  async function run(fn?: DestructiveActionGuardProps['onArchive'], after?: () => void): Promise<void> {
    if (!fn || disabled) return;
    setWorking(true);
    try {
      const ok = await fn();
      if (ok !== false) after?.();
    } finally {
      setWorking(false);
    }
  }

  if (archived) {
    return (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <button
          ref={triggerRef}
          type="button"
          className="pf-btn pf-btn--sm pf-btn--soft"
          disabled={disabled}
          aria-label={t('own.restoreAria', { entity: entityLabel })}
          onClick={() => void run(onRestore)}
        >
          {working ? <Loader2 size={13} className="pf-spin" /> : <ArchiveRestore size={13} />} {t('own.restore')}
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', position: 'relative' }}>
      {/* Inline undo (aria-live) after a successful archive. */}
      {justArchived ? (
        <span className="pf-badge pf-badge--seed" role="status" aria-live="polite" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {t('own.archived')}
          <button
            type="button"
            className="pf-btn pf-btn--sm pf-btn--ghost"
            style={{ height: 24, padding: '0 8px' }}
            disabled={disabled}
            aria-label={t('own.undoArchiveAria', { entity: entityLabel })}
            onClick={() => void run(onRestore, () => setJustArchived(false))}
          >
            {t('own.undo')}
          </button>
        </span>
      ) : (
        onArchive && (
          <button
            type="button"
            className="pf-btn pf-btn--sm"
            disabled={disabled}
            aria-label={t('own.archiveAria', { entity: entityLabel })}
            onClick={() => void run(onArchive, () => setJustArchived(true))}
          >
            {working && !confirming ? <Loader2 size={13} className="pf-spin" /> : <Archive size={13} />} {t('own.archive')}
          </button>
        )
      )}

      {onDelete && (
        <button
          ref={triggerRef}
          type="button"
          className="pf-btn pf-btn--sm pf-btn--danger"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-label={t('own.deleteAria', { entity: entityLabel })}
          onClick={() => setConfirming(true)}
        >
          <Trash2 size={13} /> {t('own.delete')}
        </button>
      )}

      {confirming && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(8,8,14,0.55)', zIndex: 998 }}
            aria-hidden="true"
            onClick={close}
          />
          <div
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dag-title"
            aria-describedby="dag-body"
            style={{
              position: 'fixed', zIndex: 999, insetInlineStart: '50%', top: '50%',
              transform: 'translate(-50%, -50%)', width: 'min(420px, calc(100vw - 32px))',
              background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
              padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 24px 64px rgba(0,0,0,0.32)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="pf-bubble" aria-hidden="true" style={{ width: 34, height: 34, background: 'var(--err-soft)', color: 'var(--color-danger)' }}>
                <AlertTriangle size={17} />
              </span>
              <span id="dag-title" style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg)' }}>{t(confirmTitleKey, { entity: entityLabel })}</span>
            </span>
            <p id="dag-body" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--fg-muted)' }}>{t(confirmBodyKey, { entity: entityLabel })}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" className="pf-btn pf-btn--sm" onClick={close} disabled={working}>{t('own.cancel')}</button>
              <button
                ref={confirmBtnRef}
                type="button"
                className="pf-btn pf-btn--sm pf-btn--danger"
                disabled={working}
                onClick={() => void run(onDelete, close)}
              >
                {working ? <Loader2 size={13} className="pf-spin" /> : <Trash2 size={13} />} {t('own.deleteConfirm')}
              </button>
            </div>
          </div>
        </>
      )}
    </span>
  );
}
